import React, { useState, useEffect, ReactElement } from 'react';
import styled from 'styled-components';
import { FlowjoCommons } from '@portal/portal-core';
import { FjCheckbox } from 'components/primitives';
import { getCountryOptions } from 'utils';
import {
  filterCountriesBySearch,
  getAddedCountries,
  getRemovedCountries,
  toggleCountryInSet,
  formatEmbargoedCountries,
} from './country-whitelist-editor-utils';
import {
  validateCountryCodesWithEmbargoWarning,
  filterEmbargoedCountries,
} from './validation-utils';

const { Regional } = FlowjoCommons;
const { embargoedCountries } = Regional;

interface CountryOption {
  name: string;
  code: string;
}

// getCountryOptions comes from an untyped .js module; annotate the callback shape here.
const COUNTRY_OPTIONS: CountryOption[] = getCountryOptions().map(
  (country: { label: string; value: string }) => ({
    name: country.label,
    code: country.value,
  }),
);

interface CountryWhitelistEditorProps {
  currentCountries?: string[];
  onSave: (addedOrKeptCountries: string[], removedCountries: string[]) => void;
  onCancel: () => void;
}

const CountryWhitelistEditor = ({
  currentCountries = [],
  onSave,
  onCancel,
}: CountryWhitelistEditorProps): ReactElement => {
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(
    new Set(currentCountries),
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [validationWarning, setValidationWarning] = useState('');

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onCancel]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const handleToggle = (countryCode: string): void => {
    const newSet = toggleCountryInSet(selectedCountries, countryCode);
    setSelectedCountries(newSet);

    // Check for embargoed countries when toggling
    const selectedArray = Array.from(newSet);
    if (selectedArray.length > 0) {
      const validation = validateCountryCodesWithEmbargoWarning(selectedArray);
      if (validation.warning) {
        setValidationWarning(validation.warning);
      } else {
        setValidationWarning('');
      }
    } else {
      setValidationWarning('');
    }
  };

  const handleSave = (): void => {
    const addedCountries = getAddedCountries(currentCountries, selectedCountries);
    const removedCountries = getRemovedCountries(currentCountries, selectedCountries);

    // Pre-validate before sending to API
    const selectedArray = Array.from(selectedCountries);
    if (selectedArray.length > 0) {
      const validation = validateCountryCodesWithEmbargoWarning(selectedArray);
      if (!validation.valid) {
        setValidationWarning(validation.error || 'Invalid country codes selected');
        return;
      }
      // Filter out embargoed countries before saving
      if (validation.embargoedCodes && validation.embargoedCodes.length > 0) {
        const { nonEmbargoedCodes } = filterEmbargoedCountries(selectedArray);
        onSave(nonEmbargoedCodes, removedCountries);
        return;
      }
    }

    onSave(addedCountries, removedCountries);
  };

  const filteredCountries = filterCountriesBySearch(COUNTRY_OPTIONS, searchTerm);

  return (
    <ModalBackdrop onClick={handleBackdropClick}>
      <EditorContainer>
        <Header>
          <Title>Edit Whitelisted Countries</Title>
          <Subtitle>
            Portal will check the license country code against the whitelist for a product version
            before authorizing access to the product version. If no countries are selected, Portal
            will default to allowing access to all licenses for that product name and version.
            {Object.keys(embargoedCountries).length > 0 && (
              <>
                {' '}
                Per company policy, embargoed countries not included for license creation or
                whitelisting are:{' '}
                {formatEmbargoedCountries(embargoedCountries, Regional.getCountryName)}.
              </>
            )}
          </Subtitle>
        </Header>

        {validationWarning && (
          <WarningMessage data-test="validation-warning">{validationWarning}</WarningMessage>
        )}

        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search countries..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <CountryGrid>
          {filteredCountries.map((country) => (
            <CountryItem key={country.code}>
              <FjCheckbox
                label={
                  <CountryLabel>
                    <CountryCode>{country.code}</CountryCode>
                    <CountryName>{country.name}</CountryName>
                  </CountryLabel>
                }
                checked={selectedCountries.has(country.code)}
                onChange={() => handleToggle(country.code)}
                bgColor="white"
              />
            </CountryItem>
          ))}
        </CountryGrid>

        <SelectedSection>
          <SelectedTitle>Selected Countries ({selectedCountries.size}):</SelectedTitle>
          <SelectedCountries>
            {selectedCountries.size === 0 ? (
              <EmptyState>All countries allowed (no restrictions)</EmptyState>
            ) : (
              Array.from(selectedCountries)
                .sort()
                .map((code) => (
                  <CountryTag key={code}>
                    {code}
                    <RemoveButton onClick={() => handleToggle(code)}>&times;</RemoveButton>
                  </CountryTag>
                ))
            )}
          </SelectedCountries>
        </SelectedSection>

        <ButtonContainer>
          <CancelButton onClick={onCancel}>Cancel</CancelButton>
          <SaveButton onClick={handleSave}>Save Changes</SaveButton>
        </ButtonContainer>
      </EditorContainer>
    </ModalBackdrop>
  );
};

export default CountryWhitelistEditor;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Above the table-toolbar dropdown (1002) and the FjTooltip visor (.fj-tooltip-portal .visor,
     1001) - both share this component's stacking context via document.body portals, and a modal
     must render above either if one is left open/lingering underneath it. */
  z-index: 1003;
`;

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  max-width: 800px;
  height: 80vh;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black};
`;

const WarningMessage = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 12px 16px;
  color: #856404;
  font-size: 14px;
  line-height: 1.5;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.selectableLightBg};
    outline-offset: 2px;
  }
`;

const CountryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 8px;
  flex: 1;
  min-height: 200px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.notSelectable};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.white};
`;

const CountryItem = styled.div`
  padding: 4px;
`;

const CountryLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CountryCode = styled.span`
  font-weight: 600;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.selectableLightBg};
  min-width: 24px;
`;

const CountryName = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black};
`;

const SelectedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.tabBackground};
  border-radius: 4px;
  max-height: 150px;
  min-height: 80px;
  flex-shrink: 0;
`;

const SelectedTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const SelectedCountries = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  align-content: flex-start;
`;

const EmptyState = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.productTitle};
  font-style: italic;
`;

const CountryTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background-color: ${({ theme }) => theme.colors.selectableLightBg};
  color: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.notSelectable};
  flex-shrink: 0;
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.selectableLightBg};
    outline-offset: 2px;
  }
`;

const CancelButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  color: ${({ theme }) => theme.colors.selectableLightBg};

  &:hover {
    background-color: ${({ theme }) => theme.colors.tabBackground};
  }
`;

const SaveButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.selectableLightBg};
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    opacity: 0.9;
  }
`;
